import {
  breakpointSizes,
  getBreakpointOnly,
  getBreakpointUp,
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { Location } from 'history'
import React from 'react'
import { useSelector } from 'react-redux'
import { AnyAction } from 'redux'
import { ThunkDispatch } from 'redux-thunk'
import { CreateVirtualCardScreen } from 'src/app/CreateVirtualCardForm/CreateVirtualCardScreen'
import Buildings from 'src/assets/images/Buildings.svg'
import APIProductEmptyStatePending from 'src/assets/svgs/APIProductEmptyStatePending.svg'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import StatusLabel from 'src/components/InfoPill/InfoPill'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import TestId from 'src/components/TestId/TestId'
import { ImageNames } from 'src/enums'
import { OrganizationRoles } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import { translateNode } from 'src/localization/translateNode'
import { UserFeature } from 'src/models/user.models'
import {
  addExpenseUrl,
  createBudgetUrl,
  createVirtualCardUrl,
  departmentsUrl,
  registerAccountUrl,
  requestVirtualCardUrl,
} from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { useQueryOwnUser } from 'src/queries/users.queries'
import {
  createSetIsCreateExpenseReportPopupOpen,
  createSetIsSetupOrganizationPopupOpen,
} from 'src/redux/actions/app.actions'
import { LocationState, RootState } from 'src/redux/createRootReducer'
import { selectIsUserOrglessRCNOrBudgetOwner } from 'src/redux/selectors/auth.selectors'
import { selectBrandFeaturesFromState } from 'src/redux/selectors/session.selectors'
import { returnImage } from 'src/utils/images.utils'
import { css } from 'styled-components'

export enum TableEmptyStateDesigns {
  YourVirtualCardsActive,
  YourVirtualCardsInactive,
  AccountTransactions,
  VirtualCardTransactions,
  RecentTransactions,
  PendingRequests,
  YourCardsTransactions,
  AccountsAccounts,
  AccountVirtualCardsActive,
  AccountVirtualCardsInactive,
  SearchVirtualCardsUnfiltered,
  SearchVirtualCardsFiltered,
  AccountBudgets,
  AccountBudgetsFiltered,
  VirtualCardsFiltered,
  NoBusinessRules,
  ApproverExpenseReports,
  SubmitterExpenseReports,
  SubmitterReportDetail,
  ApproverReportDetail,
  UserIsViewingTheirReport,
  EmptyFiltersExpenseReports,
  PendingBudgets,
  BudgetRequests,
  OrgExpenseReportCycleSettings,
  BillPay,
  BillPayFiltered,
  ExpenseCategories,
  DepartmentsEnglish,
  DepartmentsFrench,
  ExpenseCategoryLabels,
  APIProduct,
  APIProductPending,
  APIProductEmptyAPIKeys,
  APIProductDenied,
  ExpensesNeedsApproval,
}

const getTitle = (
  design: TableEmptyStateDesigns,
): React.ReactNode | undefined => {
  switch (design) {
    case TableEmptyStateDesigns.SearchVirtualCardsUnfiltered: {
      return translateNode("Looks like you don't have any cards yet.")
    }

    case TableEmptyStateDesigns.AccountVirtualCardsActive: {
      return translateNode('No cards have been created yet')
    }

    case TableEmptyStateDesigns.BillPay: {
      return translateNode('No Invoices Paid Yet')
    }

    case TableEmptyStateDesigns.OrgExpenseReportCycleSettings: {
      return translateNode("You don't have an account set up!")
    }

    case TableEmptyStateDesigns.YourVirtualCardsActive: {
      return translateNode('Looks like you need a card to spend')
    }

    case TableEmptyStateDesigns.AccountVirtualCardsInactive:
    case TableEmptyStateDesigns.YourVirtualCardsInactive: {
      return translateNode('Nothing here')
    }

    case TableEmptyStateDesigns.YourCardsTransactions:
    case TableEmptyStateDesigns.VirtualCardTransactions:
    case TableEmptyStateDesigns.AccountTransactions: {
      return translateNode('Not a penny spent')
    }

    case TableEmptyStateDesigns.RecentTransactions: {
      return translateNode('There are no transactions in the last 30 days.')
    }

    case TableEmptyStateDesigns.PendingRequests: {
      return translateNode('You have no pending requests.')
    }

    case TableEmptyStateDesigns.AccountBudgetsFiltered: {
      return translateNode('No Budgets match your search.')
    }

    case TableEmptyStateDesigns.AccountBudgets: {
      return (
        <TestId testId="noActiveBudgets">
          {translateNode('No active budgets')}
        </TestId>
      )
    }

    case TableEmptyStateDesigns.VirtualCardsFiltered: {
      return translateNode('No Virtual Cards match your search')
    }
    case TableEmptyStateDesigns.BillPayFiltered: {
      return translateNode('No Bill Pay Virtual Cards match your search')
    }
    case TableEmptyStateDesigns.NoBusinessRules: {
      return translateNode('You currently don’t have access to Business Rules')
    }

    case TableEmptyStateDesigns.ApproverExpenseReports: {
      return translateNode('No one has submitted reimbursement reports')
    }
    case TableEmptyStateDesigns.SubmitterExpenseReports: {
      return translateNode('You don’t have any reimbursement reports')
    }
    case TableEmptyStateDesigns.SubmitterReportDetail: {
      return translateNode('You don’t have any expenses in this report')
    }
    case TableEmptyStateDesigns.ApproverReportDetail: {
      return translateNode('There are no expenses in this report')
    }
    case TableEmptyStateDesigns.UserIsViewingTheirReport: {
      return translateNode('You don’t have any expenses in this report')
    }

    case TableEmptyStateDesigns.EmptyFiltersExpenseReports: {
      return translateNode(
        'You don’t have any reimbursement reports that meet your search criteria',
      )
    }

    case TableEmptyStateDesigns.AccountsAccounts: {
      return translateNode('No Accounts match your search.')
    }

    case TableEmptyStateDesigns.BudgetRequests: {
      return translateNode('No requests')
    }

    case TableEmptyStateDesigns.PendingBudgets: {
      return translateNode('No pending budgets')
    }

    case TableEmptyStateDesigns.ExpenseCategories: {
      return translateNode('No Expense Categories match your search')
    }

    case TableEmptyStateDesigns.ExpenseCategoryLabels: {
      return translateNode('No Expense Labels match your search')
    }

    case TableEmptyStateDesigns.DepartmentsEnglish:
    case TableEmptyStateDesigns.DepartmentsFrench: {
      return translateNode('Create teams for your organization')
    }

    case TableEmptyStateDesigns.APIProductPending: {
      return (
        <div>
          {translateNode('Set up API Keys')}
          <StatusLabel text={'Pending'} statusType={'INFO'} />
        </div>
      )
    }
    case TableEmptyStateDesigns.APIProduct: {
      return translateNode('Set up API Keys')
    }
    case TableEmptyStateDesigns.APIProductEmptyAPIKeys: {
      return translateNode('Generate API Key')
    }
    case TableEmptyStateDesigns.APIProductDenied: {
      return translateNode('Request Denied')
    }

    case TableEmptyStateDesigns.ExpensesNeedsApproval: {
      return translateNode("You're up to date!")
    }
  }
}

const getSecondaryMessages = (
  design: TableEmptyStateDesigns,
  isGuestUser: boolean,
  hasVirtualCardFeatures: boolean,
): React.ReactElement[] | undefined => {
  switch (design) {
    case TableEmptyStateDesigns.YourVirtualCardsActive: {
      return [
        <React.Fragment key={0}>
          {translateNode(
            "Request a card from an account holder or send one to yourself if that's you.",
          )}
        </React.Fragment>,
      ]
    }

    case TableEmptyStateDesigns.YourVirtualCardsInactive: {
      return [
        <React.Fragment key={0}>
          {translateNode(
            "This is where you'll see all your cards once they're expired or deactivated.",
          )}
        </React.Fragment>,
      ]
    }

    case TableEmptyStateDesigns.AccountVirtualCardsActive: {
      return [
        <React.Fragment key={0}>
          {translateNode(
            "Here is where you'll view or manage all the virtual cards within your account.",
          )}
        </React.Fragment>,
      ]
    }

    case TableEmptyStateDesigns.OrgExpenseReportCycleSettings: {
      return [
        <React.Fragment key={0}>
          {translateNode(
            'Register your company credit card account so you can start using Automated Expense Reports.',
          )}
        </React.Fragment>,
      ]
    }

    case TableEmptyStateDesigns.AccountVirtualCardsInactive: {
      return [
        <React.Fragment key={0}>
          {translateNode(
            "This is where you'll see all the cards that have expired or have been deactivated.",
          )}
        </React.Fragment>,
      ]
    }

    case TableEmptyStateDesigns.YourCardsTransactions:
    case TableEmptyStateDesigns.VirtualCardTransactions:
    case TableEmptyStateDesigns.AccountTransactions: {
      return [
        <React.Fragment key={0}>
          {translateNode('There are no transactions to show yet.')}
        </React.Fragment>,
      ]
    }

    case TableEmptyStateDesigns.RecentTransactions: {
      if (isGuestUser || !hasVirtualCardFeatures) {
        return undefined
      }
      return [
        <React.Fragment key={0}>
          {translateNode(
            'You can use a virtual card like any other business credit card. Request a virtual card to pay for an expense, like airfare or a software subscription.',
          )}
        </React.Fragment>,
      ]
    }

    case TableEmptyStateDesigns.AccountBudgets: {
      return [
        <React.Fragment key={0}>
          {translateNode(
            'Allocate budgets to others and manage them all here.',
          )}
        </React.Fragment>,
      ]
    }

    case TableEmptyStateDesigns.AccountBudgetsFiltered:
    case TableEmptyStateDesigns.VirtualCardsFiltered:
    case TableEmptyStateDesigns.BillPayFiltered:
    case TableEmptyStateDesigns.ExpenseCategories:
    case TableEmptyStateDesigns.ExpenseCategoryLabels: {
      return [
        <React.Fragment key={0}>
          {translateNode(
            'Check that your search is correct. You can also try broadening your search.',
          )}
        </React.Fragment>,
      ]
    }

    case TableEmptyStateDesigns.NoBusinessRules: {
      return [
        <React.Fragment key={0}>
          {translateNode(
            'In the near future, your Organization will have access to more enhanced features to support your growing business needs.',
          )}
        </React.Fragment>,
      ]
    }

    case TableEmptyStateDesigns.ApproverExpenseReports: {
      return [
        <React.Fragment key={0}>
          {translateNode('Submitted reports will appear here.')}
        </React.Fragment>,
      ]
    }
    case TableEmptyStateDesigns.SubmitterExpenseReports: {
      return [
        <React.Fragment key={0}>
          {translateNode(
            'You can create your first reimbursement report in the ‘Create New’ dropdown menu or click on the button below.',
          )}
        </React.Fragment>,
      ]
    }
    case TableEmptyStateDesigns.SubmitterReportDetail: {
      return [
        <React.Fragment key={0}>
          {translateNode(
            'You can add expenses to this report by clicking ‘+ Add Expense’ in the top right or down below.',
          )}
        </React.Fragment>,
      ]
    }
    case TableEmptyStateDesigns.ApproverReportDetail: {
      return [
        <React.Fragment key={0}>
          {translateNode(
            'Looks like the submitter of this report didn’t add any expenses to it.',
          )}
        </React.Fragment>,
      ]
    }

    case TableEmptyStateDesigns.AccountsAccounts: {
      return [
        <React.Fragment key={0}>
          {translateNode(
            'Check that your search is correct. You can also try broadening your search.',
          )}
        </React.Fragment>,
      ]
    }

    case TableEmptyStateDesigns.BudgetRequests: {
      return [
        <React.Fragment key={0}>
          {translateNode('You have no requests')}
        </React.Fragment>,
      ]
    }

    case TableEmptyStateDesigns.PendingBudgets: {
      return [
        <React.Fragment key={0}>
          {translateNode('You have no pending budgets')}
        </React.Fragment>,
      ]
    }

    case TableEmptyStateDesigns.BillPay: {
      return [
        <React.Fragment key={0}>
          {translateNode(
            'Want to pay an invoice? Create a virtual card to pay your invoices.',
          )}
        </React.Fragment>,
      ]
    }

    case TableEmptyStateDesigns.DepartmentsEnglish: {
      return [
        <React.Fragment key={0}>
          {translateNode(
            'There are no teams set up for your organization. Click "Add Team" to create one. You will be able to add members of your organization to a team once it has been created.',
          )}
        </React.Fragment>,
        <React.Fragment key={1}>
          {translateNode('Learn more about Teams in the ${resourceCenter}.', {
            resourceCenter: (
              <ResourceCenterLink
                size={HoverableLinkSizes.Large}
                underline
                regular
                analytics={{
                  event:
                    'departments - departments helpcenter link - text link press',
                  properties: {
                    interaction: 'text link press',
                    intent: 'departments helpcenter link',
                    screen: departmentsUrl(':id'),
                  },
                }}
              />
            ),
          })}
        </React.Fragment>,
      ]
    }
    case TableEmptyStateDesigns.DepartmentsFrench: {
      return [
        <React.Fragment key={0}>
          {translateNode(
            'There are no teams set up for your organization. Click "Add Team" to create one. You will be able to add members of your organization to a team once it has been created.',
          )}
        </React.Fragment>,
      ]
    }

    case TableEmptyStateDesigns.APIProductPending: {
      return [
        <React.Fragment key={0}>
          {translateNode(
            'A member of our team is currently reviewing your request and will respond as soon as possible.',
          )}
        </React.Fragment>,
      ]
    }
    case TableEmptyStateDesigns.APIProductEmptyAPIKeys:
      return [
        <React.Fragment key={0}>
          {translateNode(
            'Your request to obtain an API key has been approved. Click “Generate API Keys” to create your API key and secret.',
          )}
        </React.Fragment>,
      ]
    case TableEmptyStateDesigns.APIProduct: {
      return [
        <React.Fragment key={0}>
          {translateNode(
            'Looks like you have no API Keys. Click the button below to get started.',
          )}
        </React.Fragment>,
      ]
    }
    case TableEmptyStateDesigns.APIProductDenied: {
      return [
        <React.Fragment key={0}>
          {translateNode(
            'Your request to obtain an API key has been denied. Please contact support for more information.',
          )}
        </React.Fragment>,
      ]
    }

    case TableEmptyStateDesigns.ExpensesNeedsApproval: {
      return [
        <React.Fragment key={0}>
          {translateNode("We'll notify you when new items need review.")}
        </React.Fragment>,
      ]
    }
  }
}

export type TableEmptyStateActionButton =
  | TableEmptyStateActionButtonAriaSource
  | TableEmptyStateActionButtonAriaLabel

interface TableEmptyStateActionButtonShared {
  text: React.ReactNode
  to?: string | Location<LocationState>
  onClick?: React.MouseEventHandler
}

interface TableEmptyStateActionButtonAriaSource
  extends TableEmptyStateActionButtonShared {
  ariaLabelSource: string
  ariaLabel?: undefined
}

interface TableEmptyStateActionButtonAriaLabel
  extends TableEmptyStateActionButtonShared {
  ariaLabel: string
  ariaLabelSource?: undefined
}

interface TableEmptyStateProps {
  className?: string
  design: TableEmptyStateDesigns
  title?: React.ReactNode
  readOnly?: boolean
  wide?: boolean
  expenseReportId?: string
  isLink?: boolean
  buttonClickAction?: () => void
}

const TableEmptyState: React.FC<
  React.PropsWithChildren<TableEmptyStateProps>
> = (props): React.ReactNode => {
  const dispatch = useBackgroundDispatch()
  const { data: user } = useQueryOwnUser({})
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: user?.organizationId,
  })
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled
  const isGuestUser =
    user?.organizationRole === OrganizationRoles.guest && showGuestRole
  const { VIEW_VIRTUAL_CARD_FEATURES: hasVirtualCardFeatures } =
    useQueryUserFeatures([UserFeature.VIEW_VIRTUAL_CARD_FEATURES]).data || {}
  const title = props.title || getTitle(props.design)
  const secondaryMessages = getSecondaryMessages(
    props.design,
    !!isGuestUser,
    !!hasVirtualCardFeatures,
  )
  const wide = props.wide
  const submitterExpenseReports =
    props.design === TableEmptyStateDesigns.SubmitterExpenseReports
  const isExpenses =
    props.design === TableEmptyStateDesigns.ApproverExpenseReports ||
    submitterExpenseReports ||
    props.design === TableEmptyStateDesigns.SubmitterReportDetail ||
    TableEmptyStateDesigns.ApproverReportDetail
  const isUserOrglessRCNOrBudgetOwner = useSelector(
    selectIsUserOrglessRCNOrBudgetOwner,
  )
  const { grayscaleImageEnabled } = useSelector(selectBrandFeaturesFromState)

  const getImageSrc = (design: TableEmptyStateDesigns): string => {
    switch (design) {
      case TableEmptyStateDesigns.AccountVirtualCardsActive:
      case TableEmptyStateDesigns.AccountVirtualCardsInactive:
      case TableEmptyStateDesigns.PendingRequests:
      case TableEmptyStateDesigns.SearchVirtualCardsFiltered:
      case TableEmptyStateDesigns.SearchVirtualCardsUnfiltered:
      case TableEmptyStateDesigns.YourVirtualCardsActive:
      case TableEmptyStateDesigns.YourVirtualCardsInactive:
      case TableEmptyStateDesigns.VirtualCardsFiltered:
      case TableEmptyStateDesigns.BillPay:
      case TableEmptyStateDesigns.BillPayFiltered: {
        return returnImage(ImageNames.NoVirtualCards, grayscaleImageEnabled)
      }

      case TableEmptyStateDesigns.YourCardsTransactions:
      case TableEmptyStateDesigns.VirtualCardTransactions:
      case TableEmptyStateDesigns.RecentTransactions:
      case TableEmptyStateDesigns.AccountTransactions: {
        return returnImage(ImageNames.NoTransactions, grayscaleImageEnabled)
      }

      case TableEmptyStateDesigns.PendingBudgets:
      case TableEmptyStateDesigns.BudgetRequests:
      case TableEmptyStateDesigns.AccountBudgetsFiltered:
      case TableEmptyStateDesigns.AccountBudgets: {
        return returnImage(ImageNames.NoBudgets, grayscaleImageEnabled)
      }

      case TableEmptyStateDesigns.NoBusinessRules: {
        return Buildings
      }

      case TableEmptyStateDesigns.EmptyFiltersExpenseReports:
      case TableEmptyStateDesigns.UserIsViewingTheirReport:
      case TableEmptyStateDesigns.ApproverReportDetail:
      case TableEmptyStateDesigns.SubmitterReportDetail:
      case TableEmptyStateDesigns.SubmitterExpenseReports:
      case TableEmptyStateDesigns.ApproverExpenseReports: {
        return returnImage(ImageNames.NoOOPE, grayscaleImageEnabled)
      }

      case TableEmptyStateDesigns.AccountsAccounts:
      case TableEmptyStateDesigns.OrgExpenseReportCycleSettings: {
        return returnImage(ImageNames.AccountsEmptyState, grayscaleImageEnabled)
      }

      case TableEmptyStateDesigns.ExpenseCategories:
      case TableEmptyStateDesigns.ExpenseCategoryLabels: {
        return returnImage(ImageNames.NoSource, grayscaleImageEnabled)
      }

      case TableEmptyStateDesigns.DepartmentsEnglish:
      case TableEmptyStateDesigns.DepartmentsFrench: {
        return returnImage(ImageNames.BuildingsPeople, grayscaleImageEnabled)
      }

      case TableEmptyStateDesigns.APIProductPending: {
        return APIProductEmptyStatePending
      }
      case TableEmptyStateDesigns.APIProductEmptyAPIKeys:
      case TableEmptyStateDesigns.APIProductDenied:
      case TableEmptyStateDesigns.APIProduct: {
        return returnImage(
          ImageNames.APIProductEmptyState,
          grayscaleImageEnabled,
        )
      }

      case TableEmptyStateDesigns.ExpensesNeedsApproval: {
        return returnImage(ImageNames.NothingToReview, grayscaleImageEnabled)
      }
    }
  }
  const imageSrc = getImageSrc(props.design)

  const handleCreateBudgetClick = (): void => {
    if (isUserOrglessRCNOrBudgetOwner) {
      dispatch(createSetIsSetupOrganizationPopupOpen(true, createBudgetUrl()))
    }
  }

  const getActionButton = (
    design: TableEmptyStateDesigns,
    dispatch: ThunkDispatch<RootState, void, AnyAction>,
    expenseReportId: string | undefined,
    isGuestUser: boolean,
    hasVirtualCardFeatures: boolean,
  ): TableEmptyStateActionButton | undefined => {
    switch (design) {
      case TableEmptyStateDesigns.RecentTransactions:
      case TableEmptyStateDesigns.YourVirtualCardsActive: {
        if (isGuestUser || !hasVirtualCardFeatures) {
          return undefined
        }
        return {
          text: translateNode('Request a Virtual Card'),
          to: requestVirtualCardUrl(),
          ariaLabelSource: 'Request a Virtual Card',
        }
      }
      case TableEmptyStateDesigns.AccountBudgets: {
        return {
          text: translateNode('Create a Budget'),
          onClick: handleCreateBudgetClick,
          to: isUserOrglessRCNOrBudgetOwner ? undefined : createBudgetUrl(),
          ariaLabelSource: 'Create a Budget',
        }
      }
      case TableEmptyStateDesigns.AccountVirtualCardsActive: {
        return {
          text: translateNode('Create a Virtual Card'),
          to: createVirtualCardUrl(),
          ariaLabelSource: 'Create a Virtual Card',
        }
      }
      case TableEmptyStateDesigns.BillPay: {
        return {
          text: translateNode('Create a Bill Pay Virtual Card'),
          to: {
            pathname: createVirtualCardUrl(),
            search: '',
            state: {
              screen: CreateVirtualCardScreen.BillPay,
            },
            hash: '',
          },
          ariaLabelSource: 'Create a Bill Pay Virtual Card',
        }
      }
      case TableEmptyStateDesigns.OrgExpenseReportCycleSettings: {
        return {
          text: translateNode('Register Account'),
          to: registerAccountUrl(),
          ariaLabelSource: 'Register Account',
        }
      }
      case TableEmptyStateDesigns.SubmitterExpenseReports: {
        return {
          text: translateNode('Create Reimbursement Report'),
          onClick: (): void => {
            dispatch(createSetIsCreateExpenseReportPopupOpen(true))
          },
          ariaLabelSource: 'Create Reimbursement Report',
        }
      }
      case TableEmptyStateDesigns.SubmitterReportDetail: {
        if (expenseReportId !== undefined) {
          return {
            text: translateNode('+ Add Expense'),
            to: addExpenseUrl(expenseReportId),
            ariaLabelSource: 'Add Expense',
          }
        } else {
          return undefined
        }
      }
      case TableEmptyStateDesigns.DepartmentsEnglish:
      case TableEmptyStateDesigns.DepartmentsFrench: {
        // TODO: update URL when add depts modal is built out
        return {
          text: translateNode('+ Add Team'),
          // to: '/departments/add',
          onClick: () => props.buttonClickAction && props.buttonClickAction(),
          ariaLabelSource: 'Add Team',
        }
      }

      case TableEmptyStateDesigns.APIProduct: {
        return {
          text: translateNode('Set up API Keys'),
          onClick: () => props.buttonClickAction && props.buttonClickAction(),
          ariaLabelSource: 'Set up API Keys',
        }
      }

      case TableEmptyStateDesigns.APIProductDenied:
      case TableEmptyStateDesigns.APIProductPending: {
        return undefined
      }
      case TableEmptyStateDesigns.APIProductEmptyAPIKeys: {
        return {
          text: translateNode('Generate API Key'),
          onClick: () => props.buttonClickAction && props.buttonClickAction(),
          ariaLabelSource: 'Generate API Key',
        }
      }
      default: {
        return undefined
      }
    }
  }

  const actionButton = getActionButton(
    props.design,
    dispatch,
    props.expenseReportId,
    !!isGuestUser,
    !!hasVirtualCardFeatures,
  )

  return (
    <div
      className={props.className}
      css={css`
        width: 100%;
        display: flex;
        justify-content: center;
      `}
      data-testid={'emptyTable'}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          max-width: ${wide ? `558px` : `380px`};
          ${getBreakpointUp(breakpointSizes.XSM)(css`
            padding: 36px 0px;
          `)}
          ${getBreakpointOnly(breakpointSizes.XXSM)(css`
            padding: 36px 20px;
          `)}
        `}
      >
        <img src={imageSrc} alt={''} />
        <span
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )}
            text-align: center;
            margin-top: 30px;
            ${isExpenses &&
            css`
              margin-top: 10px;
            `}
          `}
        >
          {title}
        </span>
        {secondaryMessages?.map((message, i) => (
          <span
            key={i}
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )}
              text-align: center;
              margin-top: 20px;
              ${submitterExpenseReports &&
              css`
                max-width: 456px;
              `}
              ${props.design === TableEmptyStateDesigns.SubmitterReportDetail &&
              css`
                max-width: 350px;
              `}
            `}
          >
            {message}
          </span>
        ))}
        {actionButton !== undefined &&
          !props.readOnly &&
          (props.isLink ? (
            <HoverableLink
              css={css`
                margin-top: 30px;
              `}
              ariaLabelSource={
                (actionButton as TableEmptyStateActionButtonAriaSource)
                  .ariaLabelSource
              }
              to={actionButton.to}
              testId={'emptyTableLink'}
            >
              {actionButton.text}
            </HoverableLink>
          ) : (
            <Button
              linkProps={
                actionButton.to !== undefined
                  ? {
                      to: actionButton.to,
                      ...(actionButton.ariaLabelSource !== undefined
                        ? { ariaLabelSource: actionButton.ariaLabelSource }
                        : { ariaLabel: actionButton.ariaLabel }),
                    }
                  : undefined
              }
              className={'action-button'}
              css={css`
                margin-top: 30px;
              `}
              design={ButtonDesigns.designCta}
              type={ButtonTypes.button}
              text={actionButton.text}
              onClick={
                actionButton.onClick !== undefined
                  ? actionButton.onClick
                  : undefined
              }
              big={submitterExpenseReports}
              testId={'emptyTableBtn'}
            />
          ))}
      </div>
    </div>
  )
}

export default TableEmptyState
